import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

import { DynamicLayoutComponent } from './core/layouts/dynamic/dynamic-layout.component';
import { SignInComponent } from './components/sign-in/sign-in.component';


import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { AuthGuard } from "./shared/guard/auth.guard";

//ADMIN ROUTES
import { StoreOwnersComponent as AdminStoreOwnersComponent } from './components/store-owners/store-owners/store-owners.component';
import { HomeComponent } from './components/home/home.component';
import { StoreHomeComponent } from './components/store-home/store-home.component';
import { StoreRecipesComponent as FrontRecipesComponent } from './components/store-recipes/store-recipes.component';
import { StoreCouponsComponent as FrontCouponsComponent } from './components/store-coupons/store-coupons.component';
import { StoreBundlesComponent } from './components/store-bundles/store-bundles.component';
import { StoreMenuComponent } from './components/store-menu/store-menu.component';
import { StoreCommunityComponent } from './components/store-community/store-community.component';
import { StoreCircularComponent } from './components/store-circular/store-circular.component';
import { EditStoreProductComponent } from './components/store-owners/edit-store-product/edit-store-product.component';
import { EditBundleProductComponent } from './components/store-owners/edit-bundle-product/edit-bundle-product.component';
import { EditMenuProductComponent } from './components/store-owners/edit-menu-product/edit-menu-product.component';
import { EditCommunityProductComponent } from './components/store-owners/edit-community-product/edit-community-product.component';
import { PagenotfoundComponent } from './components/pagenotfound/pagenotfound.component';
import { StoreRestaurantComponent } from './components/store-restaurant/store-restaurant.component';
import { EditRestaurantProductComponent } from './components/store-owners/edit-restaurant-product/edit-restaurant-product.component';

const routes: Routes = [
  // Static Routes (most specific first)
  { path: '', component: HomeComponent },
  { path: 'login', component: SignInComponent },
  { path: 'sign-in', component: SignInComponent }, // Alias for consistency
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: '404', component: PagenotfoundComponent },
  { path: 'store-owner', component: AdminStoreOwnersComponent, canActivate: [AuthGuard] },

  // 4-segment Routes
  { path: ':store_name/coupons/list/:list_id', component: FrontCouponsComponent },
  { path: ':store_name/recipes/list/:list_id', component: FrontRecipesComponent },
  { path: ':store_name/community/list/:list_id', component: StoreCommunityComponent },

  // 3-segment Routes - Edit routes (protected, could be lazy-loaded for better performance)
  { path: ':store_name/bundles/:id/edit', component: EditBundleProductComponent, canActivate: [AuthGuard] },
  { path: ':store_name/menu/:id/edit', component: EditMenuProductComponent, canActivate: [AuthGuard] },
  { path: ':store_name/community/:id/edit', component: EditCommunityProductComponent, canActivate: [AuthGuard] },
  { path: ':store_name/restaurant/:id/edit', component: EditRestaurantProductComponent, canActivate: [AuthGuard] },
  { path: ':store_name/bundles/edit', component: EditBundleProductComponent, canActivate: [AuthGuard] },
  { path: ':store_name/menu/edit', component: EditMenuProductComponent, canActivate: [AuthGuard] },
  { path: ':store_name/community/edit', component: EditCommunityProductComponent, canActivate: [AuthGuard] },
  { path: ':store_name/restaurant/edit', component: EditRestaurantProductComponent, canActivate: [AuthGuard] },
  { path: ':store_name/:id/edit', component: EditStoreProductComponent, canActivate: [AuthGuard] },
  
  // 3-segment Routes - View routes
  { path: ':store_name/bundles/:id', component: StoreBundlesComponent },
  { path: ':store_name/menu/:id', component: StoreMenuComponent },
  { path: ':store_name/community/:id', component: StoreCommunityComponent },
  { path: ':store_name/restaurant/:id', component: StoreRestaurantComponent },
  { path: ':store_name/list/:list_id', component: StoreCircularComponent, data: { showAppList: true } },
  { path: ':store_name/coupons/:id', component: FrontCouponsComponent },
  { path: ':store_name/recipes/:id', component: FrontRecipesComponent },
  { path: ':store_name/recipes/:recipe_categories', component: FrontRecipesComponent },

  // 2-segment Routes
  { path: ':store_name/edit', component: EditStoreProductComponent, canActivate: [AuthGuard] },
  { path: ':store_name/bundles', component: StoreBundlesComponent, data: { showHeaderBundle: true } },
  { path: ':store_name/menu', component: StoreMenuComponent, data: { showHeaderBundle: true } },
  { path: ':store_name/community', component: StoreCommunityComponent, data: { showHeaderBundle: true } },
  { path: ':store_name/restaurant', component: StoreRestaurantComponent, data: { showHeaderBundle: true } },
  { path: ':store_name/coupons', component: FrontCouponsComponent, data: { showHeaderCoupon: true } },
  { path: ':store_name/recipes', component: FrontRecipesComponent },
  { path: ':store_name/:id', component: StoreCircularComponent },

  // 1-segment Route (catch-all for store names)
  { path: ':store_name', component: StoreCircularComponent, data: { showHeaderStore: true } },


  // Admin dashboard routes (lazy-loaded for better performance)
  {
    path: '',
    component: DynamicLayoutComponent,
    children: [
      {
        path: 'tables',
        loadChildren: () => import('./tables/tables.module').then(m => m.TablesModule)
      },
      {
        path: 'charts',
        loadChildren: () => import('./charts/charts.module').then(m => m.ChartsModule)
      },
      {
        path: 'utilities',
        loadChildren: () => import('./utilities/utilities.module').then(m => m.UtilitiesModule)
      },
      {
        path: 'components',
        loadChildren: () => import('./components/components.module').then(m => m.ComponentsModule)
      },
      {
        path: 'dashboards',
        loadChildren: () => import('./dashboards/dashboards.module').then(m => m.DashboardsModule)
      }
    ]
  },
  
  // Catch-all route for undefined paths (must be last)
  { path: '**', redirectTo: '404' }
];

@NgModule({
  // Only call RouterModule.forRoot() in the root AppRoutingModule (or the AppModule if that's where you register
  // top level application routes). In any other module, you must call the RouterModule.forChild method to register additional routes.
  imports: [
    RouterModule.forRoot(routes, {
      // If you want to preload all lazy routes when the app loads, uncomment the following line
      // preloadingStrategy: PreloadAllModules,
      onSameUrlNavigation: 'reload',
      // This value is required for server-side rendering to work.
      initialNavigation: 'enabledBlocking'
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule { }
